import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styles from "./Admin.module.css";
import { Modal } from "./Modal";
import {
  administerPersonAccess,
  fetchAccessGroupCatalogs,
  fetchPersonAccess,
  setPersonAccessToggles,
  switchOffPersonProvider,
  type AccessGroup,
  type AccessResult,
  type AdministerCommand,
  type ProviderAccessAdminView,
} from "../../api/providerAccess";
import { useSession } from "../../session/SessionContext";
import { allowanceResetDate, formatDate, formatDateTime } from "../consumption/formatters";
import { accessStatusKey, adminProblemText, adminReasonKey, adminStatusKey } from "../shared/accessProblem";
import { LoadingState } from "../states";
import { useToast } from "../toast/toastContext";

const EntitlementAdministerPermission = "entitlement.administer";
const ProvidersAdministerPermission = "providers.administer";

/**
 * A person's access at each provider that issues keys, for an administrator (AIA-109, US-8 and US-9): status and reason, groups,
 * key metadata, the renew and rotate toggles, pause and resume, revoking every key, switching the provider off and flagging the
 * account for deletion (only for someone holding `providers.administer`; nothing is deleted until the flagged list confirms it), and
 * the person's open manual tasks for the provider with their reason. A provider with nothing requested is not listed. Every outcome
 * of an action is a toast.
 */
export function PersonProviderAccessSection({ personId }: { personId: string }): React.JSX.Element | null {
  const [vendors, setVendors] = useState<{ vendorId: string; groups: AccessGroup[] }[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setVendors(null);
    void (async () => {
      const result = await fetchAccessGroupCatalogs();
      if (!cancelled) {
        setVendors(result.kind === "ok" ? result.response : []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [personId]);

  if (vendors === null || vendors.length === 0) {
    return null;
  }

  return (
    <>
      {vendors.map((vendor) => (
        <VendorAccess key={vendor.vendorId} personId={personId} vendorId={vendor.vendorId} groups={vendor.groups} />
      ))}
    </>
  );
}

type LoadState = { kind: "loading" } | { kind: "loaded"; view: ProviderAccessAdminView } | { kind: "hidden" } | { kind: "error"; message: string };

type Confirming = "revoke" | "switchOff" | null;

function VendorAccess({ personId, vendorId, groups }: { personId: string; vendorId: string; groups: AccessGroup[] }): React.JSX.Element | null {
  const { t } = useTranslation();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const toast = useToast();
  const { snapshot } = useSession();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const canSwitchOff = snapshot.status === "signed-in" && snapshot.permissions.includes(ProvidersAdministerPermission);

  const load = useCallback(async (): Promise<void> => {
    const result = await fetchPersonAccess(personId, vendorId);
    if (result.kind === "ok") {
      setState(result.response.status === "NotRequested" ? { kind: "hidden" } : { kind: "loaded", view: result.response });
    } else if (result.kind === "notFound") {
      setState({ kind: "hidden" });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.denied.description", { permissions: "entitlement.administer" }) });
    } else {
      setState({ kind: "error", message: adminProblemText(result, t) });
    }
  }, [personId, vendorId, t]);

  useEffect(() => {
    setState({ kind: "loading" });
    void load();
  }, [load]);

  const vendorName = t(`providerAccess.vendors.${vendorId}`, { defaultValue: vendorId });

  /** Shows the outcome as a toast, and reloads the section when the call went through. */
  const report = async (result: AccessResult<unknown>, success: string): Promise<void> => {
    if (result.kind === "ok") {
      toast.show(success, "success");
      await load();
    } else if (result.kind === "notFound") {
      toast.show(t("providerAccess.problems.error"), "error");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: EntitlementAdministerPermission }), "error");
    } else {
      toast.show(adminProblemText(result, t), "error");
    }
  };

  const administer = async (command: AdministerCommand): Promise<void> => {
    setBusy(true);
    const result = await administerPersonAccess(personId, vendorId, command);
    setBusy(false);
    await report(result, t(command === "pause" ? "providerAccess.admin.paused" : "providerAccess.admin.resumed"));
  };

  const setToggles = async (renew: boolean, rotate: boolean): Promise<void> => {
    setBusy(true);
    const result = await setPersonAccessToggles(personId, vendorId, renew, rotate);
    setBusy(false);
    await report(result, t("providerAccess.admin.togglesSaved"));
  };

  const revoke = async (): Promise<void> => {
    setBusy(true);
    const result = await administerPersonAccess(personId, vendorId, "revoke");
    setBusy(false);
    setConfirming(null);
    await report(result, t("providerAccess.admin.revoked"));
  };

  const switchOff = async (): Promise<void> => {
    setBusy(true);
    const result = await switchOffPersonProvider(personId, vendorId);
    setBusy(false);
    setConfirming(null);
    if (result.kind === "ok") {
      let message = t("providerAccess.admin.switchedOffNotFlagged", { provider: vendorName });
      if (result.response.flagged) {
        message = t("providerAccess.admin.switchedOffFlagged", { provider: vendorName });
      } else if (result.response.manualTaskRaised) {
        message = t("providerAccess.admin.switchedOffManualTask", { provider: vendorName });
      }

      toast.show(message, result.response.flagged ? "success" : "info");
      await load();
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: ProvidersAdministerPermission }), "error");
    } else if (result.kind === "notFound") {
      toast.show(t("providerAccess.problems.error"), "error");
    } else {
      toast.show(adminProblemText(result, t), "error");
    }
  };

  if (state.kind === "hidden") {
    return null;
  }

  const headingId = `person-access-${personId}-${vendorId}`;

  if (state.kind === "loading") {
    return (
      <section className={`${styles.field} ${styles.allocationBox}`} aria-labelledby={headingId}>
        <h3 id={headingId}>{t("providerAccess.admin.title", { provider: vendorName })}</h3>
        <LoadingState label={t("providerAccess.loading")} />
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className={`${styles.field} ${styles.allocationBox}`} aria-labelledby={headingId}>
        <h3 id={headingId}>{t("providerAccess.admin.title", { provider: vendorName })}</h3>
        <p role="alert" className={`${styles.feedback} ${styles.feedbackError}`}>
          {state.message}
        </p>
      </section>
    );
  }

  const view = state.view;
  const groupNames = view.groups.map((id) => groups.find((group) => group.id === id)?.displayName ?? id);
  const paused = view.status === "Paused";
  const canPause = !paused && view.status !== "SwitchedOff";
  // A key a switch-off deactivated stays inactive when the provider is granted again, so Resume is offered for it as well as after a pause.
  const hasInactiveKey = view.credentials.some((credential) => !credential.active && Date.parse(credential.expiresAt) > Date.now());
  const canResume = paused || (hasInactiveKey && view.status !== "SwitchedOff");
  const canRevoke = view.credentials.length > 0;
  // The section is listed only while the provider is on or the account is flagged, so a flag is the one thing that rules the shortcut out.
  const showSwitchOff = canSwitchOff && !view.flagOpen && view.status !== "SwitchedOff";

  return (
    <section className={`${styles.field} ${styles.allocationBox}`} aria-labelledby={headingId}>
      <h3 id={headingId}>{t("providerAccess.admin.title", { provider: vendorName })}</h3>
      <dl className={styles.allocationFigures}>
        <div className={styles.allocationFigure}>
          <dt>{t("providerAccess.admin.status")}</dt>
          <dd>
            {t(adminStatusKey(view.status))}
            {view.reason ? ` (${t(adminReasonKey(view.reason))})` : ""}
          </dd>
        </div>
        <div className={styles.allocationFigure}>
          <dt>{t("providerAccess.groups")}</dt>
          <dd>{groupNames.length > 0 ? groupNames.join(", ") : "—"}</dd>
        </div>
        <div className={styles.allocationFigure}>
          <dt>{t("providerAccess.admin.identity")}</dt>
          <dd>{view.vendorIdentity ?? "—"}</dd>
        </div>
      </dl>
      <p className={styles.note}>{t(accessStatusKey(view))}</p>
      {view.status === "Restricted" && view.reason === "AllowanceUsedUp" && (
        <p className={styles.note}>{t("providerAccess.status.resetsOn", { date: formatDate(allowanceResetDate()) })}</p>
      )}

      {view.flagOpen && view.flaggedForDeletionAt && (
        <p role="status" className={styles.note}>
          {t("providerAccess.admin.flagged", { when: formatDateTime(view.flaggedForDeletionAt), reason: view.flagReason ? t(adminReasonKey(view.flagReason)) : "—" })}
        </p>
      )}

      {view.credentials.length > 0 && (
        <table className={styles.table}>
          <caption>{t("providerAccess.keys.caption")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("providerAccess.keys.id")}</th>
              <th scope="col">{t("providerAccess.keys.created")}</th>
              <th scope="col">{t("providerAccess.keys.expires")}</th>
              <th scope="col">{t("providerAccess.keys.state")}</th>
              <th scope="col">{t("providerAccess.admin.acknowledged")}</th>
            </tr>
          </thead>
          <tbody>
            {view.credentials.map((credential) => (
              <tr key={credential.credentialId}>
                <td>{credential.credentialId}</td>
                <td>{formatDateTime(credential.createdAt)}</td>
                <td>{formatDateTime(credential.expiresAt)}</td>
                <td>{credential.active ? t("providerAccess.keys.active") : t("providerAccess.keys.inactive")}</td>
                <td>{credential.acknowledged ? t("providerAccess.admin.yes") : t("providerAccess.admin.no")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className={styles.form}>
        <label className={styles.toolbarToggle}>
          <input
            type="checkbox"
            className={styles.allocationToggle}
            checked={view.renewEnabled}
            disabled={busy}
            onChange={(event) => void setToggles(event.target.checked, view.rotateEnabled)}
          />
          {t("providerAccess.admin.renewToggle")}
        </label>
        <label className={styles.toolbarToggle}>
          <input
            type="checkbox"
            className={styles.allocationToggle}
            checked={view.rotateEnabled}
            disabled={busy}
            onChange={(event) => void setToggles(view.renewEnabled, event.target.checked)}
          />
          {t("providerAccess.admin.rotateToggle")}
        </label>
        {canResume && (
          <button type="button" className="button button-primary" disabled={busy} onClick={() => void administer("resume")}>
            {t("providerAccess.admin.resume")}
          </button>
        )}
        {canPause && (
          <button type="button" className="button button-secondary" disabled={busy} onClick={() => void administer("pause")}>
            {t("providerAccess.admin.pause")}
          </button>
        )}
        {canRevoke && (
          <button type="button" className="button button-secondary" disabled={busy} onClick={() => setConfirming("revoke")}>
            {t("providerAccess.admin.revoke")}
          </button>
        )}
        {showSwitchOff && (
          <button type="button" className="button button-secondary" disabled={busy} onClick={() => setConfirming("switchOff")}>
            {t("providerAccess.admin.switchOff")}
          </button>
        )}
      </div>

      {view.openManualTasks.length > 0 && (
        <>
          <h4>{t("providerAccess.admin.tasksTitle")}</h4>
          <ul>
            {view.openManualTasks.map((task) => (
              <li key={task.id}>
                <strong>{task.action}</strong>: {task.reason} <span className={styles.note}>({formatDateTime(task.createdAt)})</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {confirming === "revoke" && (
        <ConfirmDialog
          title={t("providerAccess.admin.revokeDialogTitle")}
          confirmLabel={t("providerAccess.admin.revokeDialogConfirm")}
          busy={busy}
          onConfirm={() => void revoke()}
          onClose={() => setConfirming(null)}
        >
          <p>{t("providerAccess.admin.revokeDialogBody", { provider: vendorName })}</p>
        </ConfirmDialog>
      )}
      {confirming === "switchOff" && (
        <ConfirmDialog
          title={t("providerAccess.admin.switchOffDialogTitle")}
          confirmLabel={t("providerAccess.admin.switchOffDialogConfirm")}
          busy={busy}
          onConfirm={() => void switchOff()}
          onClose={() => setConfirming(null)}
        >
          <p>{t("providerAccess.admin.switchOffDialogBody", { provider: vendorName })}</p>
          <p>{t("providerAccess.admin.switchOffDialogKept", { provider: vendorName })}</p>
          <p>
            <Link to="/admin/providers/provider-access">{t("providerAccess.admin.switchOffDialogLink")}</Link>
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

/** A confirmation in the shared modal. Focus starts on Cancel, so a stray Enter cannot revoke or switch anything off. */
function ConfirmDialog({
  title,
  confirmLabel,
  busy,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Modal title={title} onClose={onClose}>
      {children}
      <div className={styles.inlineControls}>
        <button type="button" className="button button-primary" disabled={busy} onClick={onConfirm}>
          {confirmLabel}
        </button>
        <button type="button" className="button button-secondary" data-autofocus onClick={onClose}>
          {t("providerAccess.admin.dialogCancel")}
        </button>
      </div>
    </Modal>
  );
}
